import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { TextField } from '../../ui/components/TextField';
import { TextArea } from '../../ui/components/TextArea';
import { sweepstakesApi, type ContestView, type PrizeTier } from '../../api/sweepstakes';
import { Leaderboard } from './Leaderboard';
import { WalletAddress } from './WalletAddress';
import { useNotificationStore } from '../../store/notificationStore';

interface ContestManagerProps {
  collectionId: string;
}

interface PrizeTierInput {
  name: string;
  description: string;
  type: 'leaderboard' | 'raffle';
  winnerCount: number;
}

// Convert local datetime-local string to ISO for the API
const localDateTimeToISO = (localDateTime: string): string => {
  return new Date(localDateTime).toISOString();
};

// Convert ISO string to datetime-local format (local timezone)
const isoToLocalDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function formatCountdownStr(target: string): string {
  const diff = new Date(target).getTime() - Date.now();
  if (diff <= 0) return 'Now';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

function useCountdown(target: string | undefined): string {
  const [display, setDisplay] = useState(() => target ? formatCountdownStr(target) : '');
  useEffect(() => {
    if (!target) return;
    setDisplay(formatCountdownStr(target));
    const interval = setInterval(() => setDisplay(formatCountdownStr(target)), 60_000);
    return () => clearInterval(interval);
  }, [target]);
  return display;
}

function formatXrpFromDrops(drops: string): string {
  const xrp = Number(drops) / 1_000_000;
  return xrp > 0 ? `${xrp.toLocaleString()} XRP` : 'None';
}

// ============================================================================
// Main Component
// ============================================================================

export const ContestManager: React.FC<ContestManagerProps> = ({ collectionId }) => {
  const [contestView, setContestView] = useState<ContestView | null>(null);
  const [pastContests, setPastContests] = useState<ContestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const { success: showSuccess, error: showError } = useNotificationStore();

  const fetchContest = async () => {
    setLoading(true);
    try {
      const data = await sweepstakesApi.getForCollectionManage(collectionId);
      setContestView(data.contest ? data as ContestView : null);
      setPastContests(data.pastContests || []);
    } catch (err) {
      console.error('Failed to fetch contest:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContest();
  }, [collectionId]);

  if (loading) {
    return <div className="text-sm text-subtext-color py-4">Loading contest data...</div>;
  }

  const isResolved = contestView?.contest.status === 'resolved';
  // Combine current resolved contest with past for the history section
  const allPastContests = isResolved && contestView
    ? [contestView, ...pastContests]
    : pastContests;

  // Create form
  if (mode === 'create') {
    return (
      <ContestForm
        collectionId={collectionId}
        onSave={() => { setMode('view'); fetchContest(); }}
        onCancel={() => setMode('view')}
        showSuccess={showSuccess}
        showError={showError}
      />
    );
  }

  // No active contest — show create option + past contests
  if (!contestView || isResolved) {
    return (
      <div className="space-y-6">
        <div className="p-4 border border-neutral-border rounded-lg">
          <h3 className="font-semibold text-default-font mb-2">Sweep Contest</h3>
          <p className="text-sm text-subtext-color mb-3">
            {isResolved
              ? 'Your previous contest has been resolved. You can create a new one.'
              : 'Create a sweep contest to incentivize buying activity on your collection.'}
          </p>
          <Button variant="brand-primary" onClick={() => setMode('create')}>
            {isResolved ? 'Create New Contest' : 'Create Contest'}
          </Button>
        </div>
        {allPastContests.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-subtext-color">Past Contests</h4>
            {allPastContests.map(cv => (
              <ResolvedContestSummary key={cv.contest.id} contestView={cv} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Existing active contest — edit form or management view
  if (mode === 'edit') {
    return (
      <ContestForm
        collectionId={collectionId}
        existingContest={contestView}
        onSave={() => { setMode('view'); fetchContest(); }}
        onCancel={() => setMode('view')}
        showSuccess={showSuccess}
        showError={showError}
      />
    );
  }

  return (
    <div className="space-y-6">
      <ContestManageView
        contestView={contestView}
        onRefresh={fetchContest}
        onEdit={() => setMode('edit')}
        showSuccess={showSuccess}
        showError={showError}
      />
      {pastContests.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-subtext-color">Past Contests</h4>
          {pastContests.map(cv => (
            <ResolvedContestSummary key={cv.contest.id} contestView={cv} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Contest Form (create + edit)
// ============================================================================

interface ContestFormProps {
  collectionId: string;
  existingContest?: ContestView;
  onSave: () => void;
  onCancel: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

const ContestForm: React.FC<ContestFormProps> = ({
  collectionId, existingContest, onSave, onCancel, showSuccess, showError,
}) => {
  const contest = existingContest?.contest;
  const isEdit = !!contest;

  // Determine what's locked based on contest status
  const now = Date.now();
  const snapshotPassed = contest?.snapshot_at ? new Date(contest.snapshot_at).getTime() <= now : false;
  const startPassed = contest ? new Date(contest.starts_at).getTime() <= now : false;
  const endPassed = contest ? new Date(contest.ends_at).getTime() <= now : false;
  const isActive = contest?.status === 'active';

  // When active, only prize tier descriptions and end time can change
  // When snapshot has passed, min price is locked
  // When start has passed, start time is locked
  const lockName = isActive;
  const lockDescription = isActive;
  const lockSnapshot = snapshotPassed;
  const lockStart = startPassed;
  const lockEnd = endPassed;
  const lockMinPrice = snapshotPassed;
  const lockTierStructure = snapshotPassed; // can't add/remove/change type after snapshot
  const lockTierDescriptions = false; // always editable

  // Form state — initialized from existing contest or empty
  const [name, setName] = useState(contest?.name ?? '');
  const [description, setDescription] = useState(contest?.description ?? '');
  const [startsAt, setStartsAt] = useState(
    contest ? isoToLocalDateTime(contest.starts_at) : ''
  );
  const [endsAt, setEndsAt] = useState(
    contest ? isoToLocalDateTime(contest.ends_at) : ''
  );
  const [snapshotAt, setSnapshotAt] = useState(
    contest?.snapshot_at && contest.snapshot_at !== contest.starts_at
      ? isoToLocalDateTime(contest.snapshot_at)
      : ''
  );
  const [minPriceXrp, setMinPriceXrp] = useState(() => {
    if (!contest) return '';
    const xrp = Number(contest.min_price_drops) / 1_000_000;
    return xrp > 0 ? String(xrp) : '';
  });
  const [prizeTiers, setPrizeTiers] = useState<PrizeTierInput[]>(() => {
    if (existingContest?.prizeTiers?.length) {
      return existingContest.prizeTiers.map(t => ({
        name: t.name,
        description: t.description,
        type: t.type,
        winnerCount: t.winner_count,
      }));
    }
    return [{ name: 'Grand Prize', description: '', type: 'leaderboard', winnerCount: 1 }];
  });
  const [submitting, setSubmitting] = useState(false);

  const addPrizeTier = () => {
    setPrizeTiers([...prizeTiers, {
      name: `Prize ${prizeTiers.length + 1}`,
      description: '',
      type: 'raffle',
      winnerCount: 1,
    }]);
  };

  const removePrizeTier = (index: number) => {
    if (prizeTiers.length <= 1) return;
    setPrizeTiers(prizeTiers.filter((_, i) => i !== index));
  };

  const updatePrizeTier = (index: number, field: keyof PrizeTierInput, value: any) => {
    const updated = [...prizeTiers];
    updated[index] = { ...updated[index], [field]: value };
    setPrizeTiers(updated);
  };

  const handleSubmit = async () => {
    if (!name || !startsAt || !endsAt || prizeTiers.some(t => !t.description)) {
      showError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        // Build only the changed/allowed fields
        const update: Record<string, any> = {};
        if (!lockName) update.name = name;
        if (!lockDescription) update.description = description || undefined;
        if (!lockSnapshot) update.snapshotAt = localDateTimeToISO(snapshotAt || startsAt);
        if (!lockStart) update.startsAt = localDateTimeToISO(startsAt);
        if (!lockEnd) update.endsAt = localDateTimeToISO(endsAt);
        if (!lockMinPrice) update.minPriceXrp = minPriceXrp ? parseFloat(minPriceXrp) : 0;
        update.prizeTiers = prizeTiers;
        await sweepstakesApi.update(contest!.id, update);
        showSuccess('Contest updated');
      } else {
        await sweepstakesApi.create({
          collectionId,
          name,
          description: description || undefined,
          snapshotAt: localDateTimeToISO(snapshotAt || startsAt),
          startsAt: localDateTimeToISO(startsAt),
          endsAt: localDateTimeToISO(endsAt),
          minPriceXrp: minPriceXrp ? parseFloat(minPriceXrp) : undefined,
          prizeTiers,
        });
        showSuccess('Contest created and scheduled');
      }
      onSave();
    } catch (err: any) {
      showError(err.message || `Failed to ${isEdit ? 'update' : 'create'} contest`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-default-font">
          {isEdit ? 'Edit Contest' : 'Create Sweep Contest'}
        </h3>
        {isEdit && contest && (
          <Badge variant="warning">{contest.status}</Badge>
        )}
      </div>

      <TextField label="Contest Name" helpText="Max 100 characters" disabled={lockName}>
        <TextField.Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={lockName}
        />
      </TextField>

      <TextArea label="Description (optional)" helpText="Max 2000 characters.">
        <TextArea.Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={lockDescription}
        />
      </TextArea>

      <p className="text-xs text-subtext-color">Times are in your local timezone ({userTimezone})</p>
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="Start Date/Time"
          helpText={lockStart ? 'Already started — cannot change' : undefined}
          disabled={lockStart}
        >
          <TextField.Input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            disabled={lockStart}
          />
        </TextField>
        <TextField
          label="End Date/Time"
          helpText={lockEnd ? 'Already ended — cannot change' : undefined}
          disabled={lockEnd}
        >
          <TextField.Input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            disabled={lockEnd}
          />
        </TextField>
      </div>

      <TextField
        label="Offer Snapshot Time (optional, defaults to start time)"
        helpText={lockSnapshot ? 'Snapshot already taken — cannot change' : 'Only NFTs listed for sale at this time will be eligible.'}
        disabled={lockSnapshot}
      >
        <TextField.Input
          type="datetime-local"
          value={snapshotAt}
          onChange={(e) => setSnapshotAt(e.target.value)}
          disabled={lockSnapshot}
        />
      </TextField>

      <TextField
        label="Minimum Purchase Price (XRP, optional)"
        helpText={lockMinPrice ? 'Cannot change after snapshot' : 'Purchases below this price won\'t count as entries'}
        disabled={lockMinPrice}
      >
        <TextField.Input
          value={minPriceXrp}
          onChange={(e) => setMinPriceXrp(e.target.value)}
          disabled={lockMinPrice}
          placeholder="0"
        />
      </TextField>

      {/* Prize Tiers */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-default-font">Prize Tiers</label>
          {!lockTierStructure && (
            <Button variant="neutral-secondary" size="small" onClick={addPrizeTier}>
              Add Tier
            </Button>
          )}
        </div>
        {lockTierStructure && (
          <p className="text-xs text-subtext-color mb-2">
            Tier name, type, and winner count are locked after the snapshot is taken. Prize descriptions can still be edited.
          </p>
        )}
        <div className="space-y-3">
          {prizeTiers.map((tier, index) => (
            <div key={index} className="p-3 bg-neutral-50 rounded-lg border border-neutral-border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-default-font">Tier {index + 1}</span>
                {!lockTierStructure && prizeTiers.length > 1 && (
                  <button onClick={() => removePrizeTier(index)} className="text-xs text-error-600 hover:underline">
                    Remove
                  </button>
                )}
              </div>
              <TextField label="Name" disabled={lockTierStructure}>
                <TextField.Input
                  value={tier.name}
                  onChange={(e) => updatePrizeTier(index, 'name', e.target.value)}
                  disabled={lockTierStructure}
                />
              </TextField>
              <TextArea label="Prize Description" helpText="Describe what the winner receives">
                <TextArea.Input
                  value={tier.description}
                  onChange={(e) => updatePrizeTier(index, 'description', e.target.value)}
                />
              </TextArea>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-default-font block mb-1">Type</label>
                  <select
                    value={tier.type}
                    onChange={(e) => updatePrizeTier(index, 'type', e.target.value)}
                    disabled={lockTierStructure}
                    className="w-full px-2 py-1.5 rounded border border-neutral-border bg-default-background text-default-font text-sm appearance-auto disabled:opacity-50"
                  >
                    <option value="leaderboard">Leaderboard (top buyers)</option>
                    <option value="raffle">Raffle (random draw)</option>
                  </select>
                </div>
                <TextField label="Winner Count" disabled={lockTierStructure}>
                  <TextField.Input
                    value={String(tier.winnerCount)}
                    onChange={(e) => updatePrizeTier(index, 'winnerCount', parseInt(e.target.value) || 1)}
                    disabled={lockTierStructure}
                  />
                </TextField>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center pt-2">
        <div className="flex-1">
          <Button variant="neutral-secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Button variant="brand-primary" onClick={handleSubmit} loading={submitting} disabled={submitting}>
          {isEdit ? 'Save Changes' : 'Create Contest'}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// Management View (existing contest)
// ============================================================================

interface ContestManageViewProps {
  contestView: ContestView;
  onRefresh: () => void;
  onEdit: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

const ContestManageView: React.FC<ContestManageViewProps> = ({
  contestView, onRefresh, onEdit, showSuccess, showError,
}) => {
  const { contest, prizeTiers, stats, winners } = contestView;

  const isScheduled = contest.status === 'draft' || contest.status === 'scheduled';
  const isSnapshotTaken = contest.status === 'snapshot_pending';
  const isLive = contest.status === 'active';
  const isCompleted = contest.status === 'ended';
  const isResolved = contest.status === 'resolved';

  const now = Date.now();
  const snapshotPassed = contest.snapshot_at ? new Date(contest.snapshot_at).getTime() <= now : false;
  const startPassed = new Date(contest.starts_at).getTime() <= now;
  const endPassed = new Date(contest.ends_at).getTime() <= now;
  const snapshotMatchesStart = contest.snapshot_at === contest.starts_at;

  const snapshotCountdown = useCountdown(!snapshotPassed && contest.snapshot_at ? contest.snapshot_at : undefined);
  const startCountdown = useCountdown(!startPassed ? contest.starts_at : undefined);
  const endCountdown = useCountdown(!endPassed ? contest.ends_at : undefined);

  // Detect missed timestamps — time passed but contest status didn't advance
  const snapshotMissed = snapshotPassed && isScheduled;
  const startMissed = startPassed && (isScheduled || isSnapshotTaken);
  const endMissed = endPassed && !isCompleted && !isResolved;
  const hasMissedTimestamps = isScheduled && (snapshotMissed || startMissed);

  // Can edit at all? Not after completed/resolved
  const canEdit = !isCompleted && !isResolved;

  // Display label and variant
  const statusLabel = isLive ? 'Live' : isCompleted ? 'Completed' : isResolved ? 'Resolved'
    : isSnapshotTaken ? 'Snapshot Taken' : 'Scheduled';
  const statusVariant = isLive ? 'success' : isCompleted || isResolved ? 'neutral'
    : isSnapshotTaken ? 'neutral' : 'warning';
  const canDelete = isScheduled || isSnapshotTaken;

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this contest?')) return;
    try {
      await sweepstakesApi.delete(contest.id);
      showSuccess('Contest deleted');
      onRefresh();
    } catch (err: any) {
      showError(err.message || 'Failed to delete contest');
    }
  };

  const [showResolveForm, setShowResolveForm] = useState(false);
  const [proofLinks, setProofLinks] = useState<string[]>(['']);
  const [resolving, setResolving] = useState(false);

  const handleDrawWinners = async () => {
    try {
      const result = await sweepstakesApi.drawWinners(contest.id);
      showSuccess(`${result.winners.length} winner(s) drawn`);
      onRefresh();
    } catch (err: any) {
      showError(err.message || 'Failed to draw winners');
    }
  };

  const handleResolve = async () => {
    // Validate proof links (filter empty, validate format)
    const validLinks = proofLinks.filter(l => l.trim());
    const bithompPattern = /^https?:\/\/(www\.)?bithomp\.com\/.+$/;
    for (const link of validLinks) {
      if (!bithompPattern.test(link)) {
        showError('All proof links must be valid Bithomp URLs (e.g. https://bithomp.com/en/tx/...)');
        return;
      }
    }

    setResolving(true);
    try {
      await sweepstakesApi.resolve(contest.id, validLinks.length > 0 ? validLinks : undefined);
      showSuccess('Contest resolved');
      onRefresh();
    } catch (err: any) {
      showError(err.message || 'Failed to resolve contest');
    } finally {
      setResolving(false);
    }
  };

  const addProofLink = () => setProofLinks([...proofLinks, '']);
  const removeProofLink = (index: number) => setProofLinks(proofLinks.filter((_, i) => i !== index));
  const updateProofLink = (index: number, value: string) => {
    const updated = [...proofLinks];
    updated[index] = value;
    setProofLinks(updated);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-default-font">{contest.name}</h3>
          {contest.description && (
            <p className="text-sm text-subtext-color mt-1">{contest.description}</p>
          )}
        </div>
        <Badge variant={statusVariant as any}>{statusLabel}</Badge>
      </div>

      {/* Missed timestamps warning */}
      {hasMissedTimestamps && (
        <div className="p-3 rounded-lg border border-warning-400 bg-warning-50 text-sm">
          <p className="font-semibold text-warning-700 mb-1">Scheduled times have passed</p>
          <p className="text-warning-600">
            The snapshot and/or start times have passed but the contest hasn't advanced. Edit the contest to update timestamps to future times, or delete it and create a new one.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-neutral-50 rounded-lg border border-neutral-border">
          <div className="text-xl font-semibold text-default-font">{stats.participants}</div>
          <div className="text-xs text-subtext-color">Participants</div>
        </div>
        <div className="text-center p-3 bg-neutral-50 rounded-lg border border-neutral-border">
          <div className="text-xl font-semibold text-default-font">{stats.entries}</div>
          <div className="text-xs text-subtext-color">Entries</div>
        </div>
        <div className="text-center p-3 bg-neutral-50 rounded-lg border border-neutral-border">
          <div className="text-xl font-semibold text-default-font">{stats.eligibleOffers}</div>
          <div className="text-xs text-subtext-color">Eligible Offers</div>
        </div>
      </div>

      {/* Schedule */}
      <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-border space-y-3">
        <h4 className="text-sm font-semibold text-default-font">Schedule</h4>
        <p className="text-xs text-subtext-color -mt-2">Times shown in {userTimezone}</p>

        <TimelineRow
          label="Offer Snapshot"
          timestamp={contest.snapshot_at}
          countdown={snapshotCountdown}
          passed={snapshotPassed}
          note={snapshotMatchesStart ? 'Same as start time' : undefined}
          completedLabel={isSnapshotTaken || isLive || isCompleted || isResolved ? 'Taken' : undefined}
          missedLabel={snapshotMissed ? 'Time passed — not taken' : undefined}
        />
        <TimelineRow
          label="Contest Start"
          timestamp={contest.starts_at}
          countdown={startCountdown}
          passed={startPassed}
          completedLabel={isLive || isCompleted || isResolved ? 'Started' : undefined}
          missedLabel={startMissed ? 'Time passed — not started' : undefined}
        />
        <TimelineRow
          label="Contest End"
          timestamp={contest.ends_at}
          countdown={endCountdown}
          passed={endPassed}
          completedLabel={isCompleted || isResolved ? 'Ended' : undefined}
          missedLabel={endMissed ? 'Time passed' : undefined}
        />
      </div>

      {/* Configuration */}
      <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-border space-y-3">
        <h4 className="text-sm font-semibold text-default-font">Configuration</h4>
        <div className="flex justify-between text-sm">
          <span className="text-subtext-color">Minimum Purchase Price</span>
          <span className="text-default-font font-medium">
            {Number(contest.min_price_drops) > 0 ? formatXrpFromDrops(contest.min_price_drops) : 'No minimum'}
          </span>
        </div>
      </div>

      {/* Prize Tiers */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-default-font">Prize Tiers ({prizeTiers.length})</h4>
        {prizeTiers.map((tier) => {
          const tierWinners = winners?.filter(w => w.prize_tier_id === tier.id) || [];
          return (
            <div key={tier.id} className="p-3 bg-neutral-50 rounded-lg border border-neutral-border">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={tier.type === 'leaderboard' ? 'success' : 'warning'}>
                  {tier.type === 'leaderboard' ? `Top ${tier.winner_count}` : `Raffle · ${tier.winner_count} winner${tier.winner_count > 1 ? 's' : ''}`}
                </Badge>
                <span className="font-semibold text-sm text-default-font">{tier.name}</span>
              </div>
              <p className="text-sm text-subtext-color">{tier.description}</p>
              {tierWinners.length > 0 && (
                <div className="mt-2 pt-2 border-t border-neutral-border space-y-1">
                  <span className="text-xs font-semibold text-subtext-color uppercase tracking-wider">Winners</span>
                  {tierWinners.map(w => (
                    <div key={w.id} className="flex items-center gap-2 text-sm">
                      <span className="text-brand-600 font-medium">
                        {w.rank ? `#${w.rank}` : 'Winner'}
                      </span>
                      <WalletAddress address={w.wallet_address} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Leaderboard */}
      {(isLive || isCompleted || isResolved) && (
        <div>
          <h4 className="text-sm font-semibold text-default-font mb-2">
            {isLive ? 'Live Leaderboard' : 'Final Leaderboard'}
          </h4>
          <Leaderboard contestId={contest.id} frozen={isCompleted || isResolved} />
        </div>
      )}

      {/* Winners auto-drawn notice */}
      {isCompleted && winners && winners.length > 0 && contest.drawn_by === 'auto' && (
        <div className="p-3 rounded-lg border border-success-400 bg-success-50/30 text-sm">
          <p className="text-success-600 font-medium">Winners were automatically drawn when the contest ended.</p>
        </div>
      )}

      {/* Fallback: Draw Winners manually if auto-draw failed */}
      {isCompleted && !winners?.length && (
        <div className="p-3 rounded-lg border border-warning-400 bg-warning-50/30 text-sm flex flex-col items-center">
          <p className="text-warning-600 font-medium mb-2">Winners were not automatically drawn. You can draw them manually.</p>
          <Button variant="brand-primary" onClick={handleDrawWinners}>
            Draw Winners
          </Button>
        </div>
      )}

      {/* Resolve form with proof links */}
      {isCompleted && winners && winners.length > 0 && !showResolveForm && (
        <div className="flex justify-center pt-2">
          <Button variant="brand-primary" size="large" onClick={() => setShowResolveForm(true)}>
            Resolve Contest
          </Button>
        </div>
      )}

      {showResolveForm && (
        <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-border space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-default-font mb-1">Resolve Contest</h4>
            <p className="text-xs text-subtext-color">
              Add Bithomp transaction links as proof that prizes have been sent to winners.
              These will be visible to all holders for transparency.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-default-font">
              Proof of Payment Links (optional)
            </label>
            {proofLinks.map((link, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => updateProofLink(index, e.target.value)}
                  placeholder="https://bithomp.com/explorer/..."
                  className="flex-1 px-3 py-1.5 rounded border border-neutral-border bg-default-background text-default-font text-sm placeholder:text-subtext-color"
                />
                {proofLinks.length > 1 && (
                  <button
                    onClick={() => removeProofLink(index)}
                    className="text-xs text-error-600 hover:underline shrink-0"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addProofLink}
              className="text-xs text-brand-600 hover:underline"
            >
              + Add another link
            </button>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button variant="neutral-secondary" onClick={() => setShowResolveForm(false)}>
              Cancel
            </Button>
            <Button variant="brand-primary" onClick={handleResolve} loading={resolving} disabled={resolving}>
              Resolve Contest
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center pt-2">
        <div className="flex-1 flex gap-3">
          {canDelete && (
            <Button variant="destructive-secondary" onClick={handleDelete}>
              Delete Contest
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          {canEdit && (
            <Button variant="neutral-secondary" onClick={onEdit}>
              Edit Contest
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Resolved Contest Summary (collapsible)
// ============================================================================

const ResolvedContestSummary: React.FC<{ contestView: ContestView }> = ({ contestView }) => {
  const [expanded, setExpanded] = useState(false);
  const { contest, prizeTiers, stats, winners } = contestView;

  return (
    <div className="border border-neutral-border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Badge variant="neutral">Resolved</Badge>
          <span className="font-medium text-default-font">{contest.name}</span>
          <span className="text-sm text-subtext-color">
            Ended {new Date(contest.ends_at).toLocaleDateString()} · {stats.entries} entries · {stats.participants} participants
          </span>
        </div>
        <span className="text-subtext-color text-sm">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-neutral-border pt-4">
          {/* Schedule */}
          <div className="text-sm text-subtext-color space-y-1">
            <div>Started: {new Date(contest.starts_at).toLocaleString()}</div>
            <div>Ended: {new Date(contest.ends_at).toLocaleString()}</div>
            {Number(contest.min_price_drops) > 0 && (
              <div>Min Price: {(Number(contest.min_price_drops) / 1_000_000).toLocaleString()} XRP</div>
            )}
          </div>

          {/* Winners */}
          {winners && winners.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-default-font">Winners</h4>
              {prizeTiers.map(tier => {
                const tierWinners = winners.filter(w => w.prize_tier_id === tier.id);
                if (tierWinners.length === 0) return null;
                return (
                  <div key={tier.id} className="p-3 bg-neutral-50 rounded-lg border border-neutral-border">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={tier.type === 'leaderboard' ? 'success' : 'warning'}>
                        {tier.type === 'leaderboard' ? `Top ${tier.winner_count}` : 'Raffle'}
                      </Badge>
                      <span className="font-semibold text-sm text-default-font">{tier.name}</span>
                    </div>
                    <p className="text-sm text-subtext-color mb-2">{tier.description}</p>
                    <div className="space-y-1">
                      {tierWinners.map(w => (
                        <div key={w.id} className="flex items-center gap-2 text-sm">
                          <span className="text-brand-600 font-medium">
                            {w.rank ? `#${w.rank}` : 'Winner'}
                          </span>
                          <WalletAddress address={w.wallet_address} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Proof Links */}
          {contest.proof_links && contest.proof_links.length > 0 && (
            <div className="p-3 bg-success-50/30 rounded-lg border border-success-200">
              <h4 className="text-sm font-semibold text-default-font mb-2">Proof of Prize Distribution</h4>
              <div className="space-y-1">
                {contest.proof_links.map((link, i) => (
                  <a
                    key={i}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-brand-600 hover:underline break-all"
                  >
                    {link}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div>
            <h4 className="text-sm font-semibold text-default-font mb-2">Final Leaderboard</h4>
            <Leaderboard contestId={contest.id} frozen />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Timeline Row
// ============================================================================

interface TimelineRowProps {
  label: string;
  timestamp: string | null;
  countdown: string;
  passed: boolean;
  note?: string;
  completedLabel?: string;
  missedLabel?: string;
}

const TimelineRow: React.FC<TimelineRowProps> = ({
  label, timestamp, countdown, passed, note, completedLabel, missedLabel,
}) => {
  if (!timestamp) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-subtext-color w-28 shrink-0">{label}</span>
      <span className="text-sm text-default-font">
        {new Date(timestamp).toLocaleString()}
      </span>
      {completedLabel ? (
        <span className="text-xs text-success-600 font-medium">✓ {completedLabel}</span>
      ) : missedLabel ? (
        <span className="text-xs text-error-600 font-medium">⚠ {missedLabel}</span>
      ) : !passed ? (
        <span className="text-xs text-subtext-color">({countdown})</span>
      ) : null}
      {note && <span className="text-xs text-subtext-color italic">— {note}</span>}
    </div>
  );
};
